import TemplateCatalogueDialog from "./TemplateCatalogueDialog";
import * as support from "./Templates.support";
import {
  AddRounded as AddIcon,
  DeleteRounded as DeleteIcon,
  CloseRounded as CloseIcon,
  ExpandMoreRounded as ExpandIcon,
  ExpandLessRounded as CollapseIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button as MuiButton,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeNewId } from "common/ids";
import * as logger from "common/logger";
import Component from "components/Component";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import TemplateValidationDialog from "components/common/TemplateValidationDialog";
import FileDetail from "components/templates/FileDetail";
import FileIcon from "components/templates/FileIcon";
import * as config from "config";
import * as fileUtils from "fileUtils";
import { ENDORSEMENT_ALLOW_SKIP_COUNT } from "flags";
import { useState, useRef } from "react";
import { useValidateTemplateMutation } from "services/docGenService";
import { useFeatureFlag } from "store/hooks/featureFlagHooks";

const NULL_SENTINEL = "__NULL__";

const SAVE_VALIDATION_FAILURE_MESSAGE =
  "Validation could not run; you may save anyway.";
const CHECK_VALIDATION_FAILURE_MESSAGE = "Validation could not run.";

export const overrideLogMessage = (errorCount, reason) =>
  `Validation override (${errorCount} error(s)): ${reason}`;

const EmailEditor = ({ email, setEmailParameter, deleteEmail }) => {
  return (
    <Card>
      <Stack direction={"column"} spacing={1} style={{ margin: 10 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <TextField
            type={"text"}
            InputLabelProps={{ shrink: true }}
            InputProps={{ spellCheck: "false" }}
            label={"Name"}
            autoComplete={"off"}
            variant={"filled"}
            value={email?.name ?? ""}
            onChange={(e) => {
              setEmailParameter("name", e.target.value);
            }}
            fullWidth
          />
          {email?.name && (
            <IconButton icon={DeleteIcon} onClick={deleteEmail} />
          )}
        </Stack>
        <TextField
          type={"text"}
          InputLabelProps={{ shrink: true }}
          InputProps={{ spellCheck: "false" }}
          label={"Subject"}
          autoComplete={"off"}
          variant={"filled"}
          value={email?.subject ?? ""}
          onChange={(e) => {
            setEmailParameter("subject", e.target.value);
          }}
          fullWidth
        />
        <TextField
          type={"text"}
          InputLabelProps={{ shrink: true }}
          InputProps={{ spellCheck: "false" }}
          label={"Recipients"}
          autoComplete={"off"}
          variant={"filled"}
          value={email?.toEmails ?? ""}
          onChange={(e) => {
            setEmailParameter("toEmails", e.target.value);
          }}
          fullWidth
        />
        <TextField
          type={"text"}
          InputLabelProps={{ shrink: true }}
          InputProps={{ spellCheck: "false" }}
          label={"CC"}
          autoComplete={"off"}
          variant={"filled"}
          value={email?.ccEmails ?? ""}
          onChange={(e) => {
            setEmailParameter("ccEmails", e.target.value);
          }}
          fullWidth
        />
      </Stack>
    </Card>
  );
};

const AllowedEmailTargetsEditor = ({ value, onChange }) => {
  const [editMode, setEditMode] = useState(false);

  const emails = (value ?? []).map((email) => ({
    ...email,
    toEmails: (email?.toEmails || []).join(", "),
    ccEmails: (email?.ccEmails || []).join(", "),
  }));

  const setEmails = (emails) => {
    const newEmails = (emails ?? [])
      .filter(
        (email) =>
          email?.name ||
          email?.subject ||
          email?.toEmails?.length ||
          email?.ccEmails?.length
      )
      .map((email) => ({
        ...email,
        toEmails: email.toEmails ? email.toEmails.split(/[\s, ]+/) : [],
        ccEmails: email.ccEmails ? email.ccEmails.split(/[\s, ]+/) : [],
      }));
    onChange(newEmails);
  };

  const setEmailParameter = (index) => (parameter, value) => {
    let newEmails;
    if (index === emails.length) {
      newEmails = [
        ...emails,
        {
          id: makeNewId(),
          [parameter]: value,
        },
      ];
    } else {
      newEmails = [
        ...emails.slice(0, index),
        {
          ...emails?.[index],
          [parameter]: value,
        },
        ...emails.slice(index + 1, emails.length),
      ];
    }
    setEmails(
      newEmails.filter((email) => {
        return (
          email?.name ||
          email?.subject ||
          (email?.toEmails || []).length > 0 ||
          (email?.ccEmails || []).length > 0
        );
      })
    );
  };

  const deleteEmail = (index) => {
    setEmails(emails.filter((email, i) => index !== i));
  };

  return editMode ? (
    <Stack spacing={2} direction={"column"}>
      <Stack spacing={1} direction={"row"} alignItems={"center"}>
        <IconButton
          icon={CollapseIcon}
          onClick={() => {
            setEditMode(false);
          }}
        />
      </Stack>
      <Stack spacing={3} direction={"column"} style={{ marginLeft: 20 }}>
        {[...emails, {}].map((email, index) => (
          <EmailEditor
            email={email}
            key={index}
            setEmailParameter={setEmailParameter(index)}
            deleteEmail={() => {
              deleteEmail(index);
            }}
          />
        ))}
      </Stack>
    </Stack>
  ) : (
    <Stack spacing={1} direction={"row"} alignItems={"center"}>
      <IconButton
        icon={ExpandIcon}
        onClick={() => {
          setEditMode(true);
        }}
      />
      <Typography style={{ marginLeft: 5 }}>
        {`Email Options: ${emails?.length || 0}`}
      </Typography>
    </Stack>
  );
};

const AttributesEditor = ({ value, onChange }) => {
  const [show, setShow] = useState(false);

  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const attributes = value ?? {};

  return (
    <>
      <Stack spacing={1} direction={"row"} alignItems={"center"}>
        <IconButton
          icon={show ? CollapseIcon : ExpandIcon}
          onClick={() => {
            setShow(!show);
          }}
        />
        <Typography style={{ marginLeft: 5 }}>
          {`Template Attributes: ${Object.keys(attributes).length}`}
        </Typography>
      </Stack>
      {show && (
        <Card style={{ marginTop: 10 }}>
          <Stack direction={"column"} spacing={2} style={{ margin: 10 }}>
            {Object.entries(attributes).map(([key, value], index) => (
              <Stack
                key={index}
                direction={"row"}
                spacing={2}
                alignItems={"center"}
              >
                <TextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ spellCheck: "false" }}
                  label={"Key"}
                  autoComplete={"off"}
                  variant={"filled"}
                  value={key}
                  disabled
                  fullWidth
                />
                <TextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ spellCheck: "false" }}
                  label={"Value"}
                  autoComplete={"off"}
                  variant={"filled"}
                  value={value}
                  onChange={(e) => {
                    const newAttributes = { ...attributes };
                    newAttributes[key] = e.target.value;
                    onChange(newAttributes);
                  }}
                  fullWidth
                />
                <IconButton
                  icon={DeleteIcon}
                  onClick={() => {
                    const newAttributes = { ...attributes };
                    delete newAttributes[key];
                    onChange(newAttributes);
                  }}
                />
              </Stack>
            ))}
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"New Key"}
                autoComplete={"off"}
                variant={"filled"}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                fullWidth
              />
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"New Value"}
                autoComplete={"off"}
                variant={"filled"}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                fullWidth
              />
              <IconButton
                icon={AddIcon}
                onClick={() => {
                  if (newKey.trim()) {
                    const newAttributes = { ...attributes };
                    newAttributes[newKey.trim()] = newValue;
                    onChange(newAttributes);
                    setNewKey("");
                    setNewValue("");
                  }
                }}
                disabled={!newKey.trim()}
              />
            </Stack>
          </Stack>
        </Card>
      )}
    </>
  );
};

const IncludedEndorsementsEditor = ({
  value,
  mode,
  onChange,
  onChangeMode,
}) => {
  const [show, setShow] = useState(false);

  const [newEndorsement, setNewEndorsement] = useState("");

  const filterMode = mode ?? "INCLUDE";

  const addEndorsement = () => {
    const endorsement = newEndorsement.trim();
    if (endorsement !== "" && !(value ?? []).includes(endorsement)) {
      onChange([...value, endorsement]);
      setNewEndorsement("");
    }
  };

  const removeEndorsement = (endorsement) => {
    onChange((value ?? []).filter((e) => e !== endorsement));
  };

  return (
    <>
      <Stack spacing={1} direction={"row"} alignItems={"center"}>
        <IconButton
          icon={show ? CollapseIcon : ExpandIcon}
          onClick={() => {
            setShow(!show);
          }}
        />
        <Typography style={{ marginLeft: 5 }}>
          {"Endorsement Filter: " +
            (value
              ? `${filterMode === "INCLUDE" ? "include" : "exclude"} list of ${
                  value?.length ?? 0
                }`
              : "[none]")}
        </Typography>
      </Stack>
      {show && (
        <Paper variant={"outlined"} sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={value != null}
                  onChange={(event) =>
                    event.target.checked ? onChange([]) : onChange(null)
                  }
                  size={"small"}
                />
              }
              label={"Filter endorsements"}
              size={"small"}
            />
            {value != null && (
              <FormControl fullWidth size={"small"}>
                <InputLabel>{"Filter Mode"}</InputLabel>
                <Select
                  value={filterMode}
                  label={"Filter Mode"}
                  onChange={(e) => onChangeMode(e.target.value)}
                >
                  <MenuItem value={"INCLUDE"}>
                    {"Include only listed endorsements"}
                  </MenuItem>
                  <MenuItem value={"EXCLUDE"}>
                    {"Exclude listed endorsements"}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
            {value != null && (
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <TextField
                  type={"text"}
                  label={"Add Endorsement"}
                  variant={"filled"}
                  value={newEndorsement}
                  onChange={(e) => setNewEndorsement(e.target.value)}
                  fullWidth
                />
                <Button
                  onClick={addEndorsement}
                  startIcon={<AddIcon />}
                  disabled={!newEndorsement.trim()}
                >
                  {"Add"}
                </Button>
              </Stack>
            )}
            {value != null &&
              ((value?.length ?? 0) > 0 ? (
                <Stack spacing={1}>
                  {value.map((endorsement, index) => (
                    <Paper key={index} variant={"outlined"} sx={{ p: 1 }}>
                      <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                      >
                        <Typography>{endorsement}</Typography>
                        <IconButton
                          icon={DeleteIcon}
                          onClick={() => removeEndorsement(endorsement)}
                          variant={"bright"}
                          tooltip={"Remove endorsement"}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography color={"text.secondary"} align={"center"}>
                  {"No endorsements added"}
                </Typography>
              ))}
          </Stack>
        </Paper>
      )}
    </>
  );
};

const VariablesEditor = ({ value, onChange }) => {
  const [show, setShow] = useState(false);

  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const variables = value ?? {};

  return (
    <>
      <Stack spacing={1} direction={"row"} alignItems={"center"}>
        <IconButton
          icon={show ? CollapseIcon : ExpandIcon}
          onClick={() => {
            setShow(!show);
          }}
        />
        <Typography style={{ marginLeft: 5 }}>
          {`Variables: ${Object.keys(variables).length}`}
        </Typography>
      </Stack>
      {show && (
        <Card style={{ marginTop: 10 }}>
          <Stack direction={"column"} spacing={2} style={{ margin: 10 }}>
            {Object.entries(variables)
              .sort()
              .map(([key, value], index) => (
                <Stack
                  key={index}
                  direction={"row"}
                  spacing={2}
                  alignItems={"center"}
                >
                  <TextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={"Key"}
                    autoComplete={"off"}
                    variant={"filled"}
                    value={key}
                    disabled
                    fullWidth
                  />
                  <TextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={"Value"}
                    autoComplete={"off"}
                    variant={"filled"}
                    value={value}
                    onChange={(e) => {
                      const newVariables = { ...variables };
                      newVariables[key] = e.target.value;
                      onChange(newVariables);
                    }}
                    fullWidth
                  />
                  <IconButton
                    icon={DeleteIcon}
                    onClick={() => {
                      const newVariables = { ...variables };
                      delete newVariables[key];
                      onChange(newVariables);
                    }}
                  />
                </Stack>
              ))}
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"New Key"}
                autoComplete={"off"}
                variant={"filled"}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                fullWidth
              />
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"New Value"}
                autoComplete={"off"}
                variant={"filled"}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                fullWidth
              />
              <IconButton
                icon={AddIcon}
                onClick={() => {
                  if (newKey.trim()) {
                    const newVariables = { ...variables };
                    newVariables[newKey.trim()] = newValue.trim();
                    onChange(newVariables);
                    setNewKey("");
                    setNewValue("");
                  }
                }}
                disabled={!newKey.trim()}
              />
            </Stack>
          </Stack>
        </Card>
      )}
    </>
  );
};

const ImageReplacementsEditor = ({ value, onChange }) => {
  const [show, setShow] = useState(false);

  const [newKey, setNewKey] = useState("");
  const [newFileIdVariable, setNewFileIdVariable] = useState("");

  const imageReplacements = value ?? {};

  return (
    <>
      <Stack spacing={1} direction={"row"} alignItems={"center"}>
        <IconButton
          icon={show ? CollapseIcon : ExpandIcon}
          onClick={() => {
            setShow(!show);
          }}
        />
        <Typography style={{ marginLeft: 5 }}>
          {`Image Replacements: ${Object.keys(imageReplacements).length}`}
        </Typography>
      </Stack>
      {show && (
        <Card style={{ marginTop: 10 }}>
          <Stack direction={"column"} spacing={2} style={{ margin: 10 }}>
            {Object.entries(imageReplacements)
              .sort()
              .map(([key, value], index) => (
                <Stack
                  key={index}
                  direction={"row"}
                  spacing={2}
                  alignItems={"center"}
                >
                  <TextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={"Key"}
                    autoComplete={"off"}
                    variant={"filled"}
                    value={key}
                    disabled
                    fullWidth
                  />
                  <TextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={"File ID Variable"}
                    autoComplete={"off"}
                    variant={"filled"}
                    value={value?.fileIdVariable ?? ""}
                    onChange={(e) => {
                      const newVariables = { ...imageReplacements };
                      newVariables[key] = { fileIdVariable: e.target.value };
                      onChange(newVariables);
                    }}
                    fullWidth
                  />
                  <IconButton
                    icon={DeleteIcon}
                    onClick={() => {
                      const newVariables = { ...imageReplacements };
                      delete newVariables[key];
                      onChange(newVariables);
                    }}
                  />
                </Stack>
              ))}
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"New Key"}
                autoComplete={"off"}
                variant={"filled"}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                fullWidth
              />
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"File ID Variable"}
                autoComplete={"off"}
                variant={"filled"}
                value={newFileIdVariable}
                onChange={(e) => setNewFileIdVariable(e.target.value)}
                fullWidth
              />
              <IconButton
                icon={AddIcon}
                onClick={() => {
                  if (newKey.trim()) {
                    const newVariables = { ...imageReplacements };
                    newVariables[newKey.trim()] = {
                      fileIdVariable: newFileIdVariable.trim(),
                    };
                    onChange(newVariables);
                    setNewKey("");
                    setNewFileIdVariable("");
                  }
                }}
                disabled={!newKey.trim()}
              />
            </Stack>
          </Stack>
        </Card>
      )}
    </>
  );
};

const FileIdEditor = ({ value, onChange, template, companyId }) => {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const download = async () => {
    setDownloading(true);
    await fileUtils.saveFile(
      value,
      `${template.title}${support.templateExtension(template.form)}`,
      companyId
    );
    setDownloading(false);
    onChange(value);
  };

  const handleFileChange = async (event) => {
    setUploading(true);
    const file = event.target.files[0];
    const newFileId = await fileUtils.createFileEntry(
      file,
      false,
      null,
      companyId
    );
    onChange(newFileId);
    setUploading(false);
  };

  return (
    <>
      <input
        type={"file"}
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      <Stack direction={"row"} justifyContent={"flex-end"} spacing={2}>
        {value && <Typography>{`File Id: ${value}`}</Typography>}
        <Button
          onClick={() => fileInputRef.current?.click?.()}
          isLoading={uploading}
        >
          {"Upload Template"}
        </Button>
        {value && (
          <Button onClick={() => download()} isLoading={downloading}>
            {"Download Template"}
          </Button>
        )}
      </Stack>
    </>
  );
};

const ReferenceTemplateEditor = ({ value, onChange, template, templates }) => {
  const options = (templates ?? []).filter(({ id }) => id !== template?.id);
  return (
    <Autocomplete
      value={
        value != null
          ? options.filter(({ id }) => id === value)?.[0] ?? {
              title: "[Invalid]",
            }
          : { title: "[Unset]" }
      }
      options={options}
      getOptionLabel={({ title }) => title}
      renderOption={(props, option) => (
        <ListItem {...props}>
          <FileDetail
            name={option.title}
            filename={option.filename}
            type={option.form}
          />
        </ListItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={"filled"}
          label={"Select Reference Template"}
          InputLabelProps={{ shrink: true }}
        />
      )}
      onChange={(_, value) => onChange(value?.id ?? null)}
      sx={{ minWidth: "20rem" }}
    />
  );
};

const FormEditor = ({ value, onChange, template, templates }) => {
  const referenceTemplateForm =
    template.templateType === "REFERENCE"
      ? templates?.filter(({ id }) => id === template?.referenceTemplateId)?.[0]
          ?.form ?? value
      : value;

  const selectedTooltip = support.templateTypes.find(
    ({ key }) => key === template.form
  )?.tooltip;

  return (
    <Stack direction={"column"} gap={2}>
      <Stack direction={"row"} alignItems={"center"} gap={1}>
        <FormControl
          variant={"filled"}
          sx={{ minWidth: 200 }}
          error={referenceTemplateForm !== value}
        >
          <InputLabel>{"Form"}</InputLabel>
          <Select
            value={template.form ?? ""}
            onChange={(e) => onChange(e.target.value)}
          >
            {support.templateTypes.map(({ key, label }) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedTooltip && (
          <Tooltip title={selectedTooltip} placement={"right"}>
            <InfoIcon
              fontSize={"small"}
              sx={{ color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        )}
      </Stack>
      {referenceTemplateForm !== value && (
        <Alert severity={"error"}>
          {`Doesn't match reference template form. `}
          <Button
            size={"small"}
            onClick={() => onChange(referenceTemplateForm)}
          >
            {"Update"}
          </Button>
        </Alert>
      )}
    </Stack>
  );
};

const TemplateEditor = ({
  template: inputTemplate,
  onSave,
  companyId,
  isOpen,
  onClose,
  reason,
  setReason,
  templates,
}) => {
  const [template, setTemplate] = useState(() => {
    const base = {
      active: true,
      title: "",
      form: "EXCEL",
      filename: "",
      attributes: {},
      variables: {},
      ...inputTemplate,
    };
    const correctExt = support.templateExtension(base.form);
    return {
      ...base,
      filename: base.filename
        ? support.replaceExtension(base.filename, correctExt)
        : base.filename,
    };
  });

  const { isOn: allowSkipCount } = useFeatureFlag(
    ENDORSEMENT_ALLOW_SKIP_COUNT,
    {
      default: true,
    }
  );

  const updateTemplate = (values) => {
    setTemplate((currentTemplate) => ({
      ...currentTemplate,
      ...values,
    }));
  };

  const [validateTemplate] = useValidateTemplateMutation();
  // Which button is waiting on the validation call: "save" or "check".
  const [validating, setValidating] = useState(null);
  // The open validation dialog: {mode: "save" | "check", report, failureMessage}.
  const [validation, setValidation] = useState(null);
  const [catalogueOpen, setCatalogueOpen] = useState(false);

  const canValidate =
    (template.form ?? "").includes("JINJA") && template.fileId != null;

  const runValidation = async () => {
    try {
      // The backend accepts exactly one identifier. With a file present we
      // validate the unsaved file and fields; only a file-less record is
      // validated by its stored id.
      const report = await validateTemplate({
        templateId: template.fileId ? undefined : template.id,
        fileId: template.fileId || undefined,
        title: template.title,
        variables: template.variables,
        imageReplacements: template.imageReplacements,
        impersonateCompanyId: companyId,
      }).unwrap();
      return { report };
    } catch (e) {
      logger.exception(e);
      return { failed: true, detail: e?.description ?? e?.message ?? null };
    }
  };

  const withDetail = (message, outcome) =>
    outcome.detail ? `${message} (${outcome.detail})` : message;

  const handleSaveClick = async () => {
    if (!canValidate) {
      onSave({ template, userLogMessage: reason });
      return;
    }
    setValidating("save");
    const outcome = await runValidation();
    setValidating(null);
    if (outcome.failed) {
      setValidation({
        mode: "save",
        report: null,
        failureMessage: withDetail(SAVE_VALIDATION_FAILURE_MESSAGE, outcome),
      });
      return;
    }
    const { report } = outcome;
    const isClean =
      report?.ok &&
      (report.errors ?? []).length === 0 &&
      (report.warnings ?? []).length === 0;
    if (isClean) {
      onSave({ template, userLogMessage: reason });
      return;
    }
    setValidation({ mode: "save", report, failureMessage: null });
  };

  const handleValidateClick = async () => {
    setValidating("check");
    const outcome = await runValidation();
    setValidating(null);
    setValidation({
      mode: "check",
      report: outcome.report ?? null,
      failureMessage: outcome.failed
        ? withDetail(CHECK_VALIDATION_FAILURE_MESSAGE, outcome)
        : null,
    });
  };

  const errorCount = validation?.report?.errors?.length ?? 0;
  const needsForce = errorCount > 0 || validation?.failureMessage != null;

  const handleProceed = () => {
    onSave({
      template,
      userLogMessage:
        errorCount > 0 ? overrideLogMessage(errorCount, reason) : reason,
      ...(needsForce && { force: true }),
    });
    setValidation(null);
  };

  return (
    <Dialog
      fullWidth
      maxWidth={"md"}
      sx={{ "& .MuiDialog-paper": { maxWidth: 980, width: 980 } }}
      open={isOpen}
    >
      <DialogContent>
        <Component
          title={template.title}
          options={<IconButton icon={CloseIcon} onClick={onClose} />}
        >
          <Stack direction={"column"} gap={2}>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <FileIcon type={template.form ?? ""} />
                <Typography>
                  {template.id ? `Id: ${template.id}` : "New Template"}
                </Typography>
              </Stack>
              <FormControlLabel
                label={"Active"}
                labelPlacement={"start"}
                control={
                  <Switch
                    checked={template.active ?? false}
                    onChange={(e) =>
                      updateTemplate({
                        active: e.target.checked,
                      })
                    }
                  />
                }
              />
            </Stack>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Stack direction={"row"} gap={2}>
                <FormControl
                  variant={"filled"}
                  sx={{ minWidth: 120, width: 120 }}
                >
                  <InputLabel>{"Type"}</InputLabel>
                  <Select
                    variant={"filled"}
                    value={template.templateType ?? NULL_SENTINEL}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateTemplate({
                        templateType: value === NULL_SENTINEL ? null : value,
                      });
                    }}
                  >
                    <MenuItem key={NULL_SENTINEL} value={NULL_SENTINEL}>
                      {"File"}
                    </MenuItem>
                    <MenuItem key={"REFERENCE"} value={"REFERENCE"}>
                      {"Reference"}
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormEditor
                  value={template.form}
                  onChange={(form) => {
                    const updates = { form, multiEntryAllowed: false };
                    const newExt = support.templateExtension(form);
                    const currentExt = support.templateExtension(template.form);
                    if (newExt !== currentExt) {
                      updates.filename = support.replaceExtension(
                        template.filename ?? "",
                        newExt
                      );
                    }
                    updateTemplate(updates);
                  }}
                  template={template}
                  templates={templates}
                />
              </Stack>
              <Stack
                direction={"column"}
                alignItems={"flex-end"}
                sx={{
                  display: [
                    "WORD_JINJA_ENDORSEMENT",
                    "PDF_ENDORSEMENT",
                  ].includes(template.form)
                    ? "flex"
                    : "none",
                }}
              >
                <FormControlLabel
                  variant={"filled"}
                  label={"Multi Entry Allowed"}
                  labelPlacement={"start"}
                  control={
                    <Switch
                      checked={template.multiEntryAllowed ?? false}
                      onChange={(e) =>
                        updateTemplate({ multiEntryAllowed: e.target.checked })
                      }
                    />
                  }
                />
                {allowSkipCount && (
                  <FormControlLabel
                    label={"Skip endorsement count"}
                    labelPlacement={"start"}
                    control={
                      <Switch
                        checked={template.skipEndorsementCount ?? false}
                        onChange={(e) =>
                          updateTemplate({
                            skipEndorsementCount: e.target.checked,
                          })
                        }
                      />
                    }
                  />
                )}
              </Stack>
            </Stack>
            {template.templateType === "REFERENCE" && (
              <ReferenceTemplateEditor
                value={template.referenceTemplateId ?? null}
                onChange={(referenceTemplateId) =>
                  updateTemplate({ referenceTemplateId })
                }
                template={template}
                templates={templates}
              />
            )}
            <TextField
              type={"text"}
              InputLabelProps={{ shrink: true }}
              InputProps={{ spellCheck: "false" }}
              label={"Display Name"}
              autoComplete={"off"}
              variant={"filled"}
              value={template.title ?? ""}
              onChange={(e) => updateTemplate({ title: e.target.value })}
              fullWidth
            />
            <TextField
              type={"text"}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                spellCheck: "false",
                endAdornment: (
                  <InputAdornment position={"end"}>
                    <Typography variant={"body2"} color={"text.secondary"}>
                      {support.templateExtension(template.form)}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              label={"Output Filename"}
              autoComplete={"off"}
              variant={"filled"}
              value={support.replaceExtension(template.filename ?? "", "")}
              onChange={(e) =>
                updateTemplate({
                  filename:
                    e.target.value + support.templateExtension(template.form),
                })
              }
              fullWidth
            />
            <AllowedEmailTargetsEditor
              value={template.allowedEmailTargets}
              onChange={(allowedEmailTargets) =>
                updateTemplate({ allowedEmailTargets })
              }
            />
            <AttributesEditor
              value={template.attributes}
              onChange={(attributes) => updateTemplate({ attributes })}
            />
            {template.form === "ENDORSEABLE_WORD_JINJA" && (
              <IncludedEndorsementsEditor
                value={template.includedEndorsements}
                mode={template.includedEndorsementsMode}
                onChange={(includedEndorsements) =>
                  updateTemplate({ includedEndorsements })
                }
                onChangeMode={(includedEndorsementsMode) =>
                  updateTemplate({ includedEndorsementsMode })
                }
              />
            )}
            <VariablesEditor
              value={template.variables}
              onChange={(variables) => updateTemplate({ variables })}
            />
            <ImageReplacementsEditor
              value={template.imageReplacements}
              onChange={(imageReplacements) =>
                updateTemplate({ imageReplacements })
              }
            />
            {template.templateType == null && (
              <FileIdEditor
                value={template.fileId ?? null}
                onChange={(fileId) => updateTemplate({ fileId })}
                template={template}
                companyId={companyId}
              />
            )}
          </Stack>
        </Component>
      </DialogContent>
      <DialogActions>
        <Stack
          direction={"row"}
          gap={2}
          alignItems={"center"}
          justifyContent={"right"}
          sx={{ width: "100%" }}
        >
          <TextField
            type={"text"}
            InputLabelProps={{ shrink: true }}
            InputProps={{ spellCheck: "false" }}
            label={"Reason for change"}
            autoComplete={"off"}
            variant={"filled"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
          />
          {canValidate && (
            <Button
              onClick={handleValidateClick}
              isLoading={validating === "check"}
              isDisabled={validating != null}
              color={"secondary"}
            >
              {"Validate"}
            </Button>
          )}
          <Button
            onClick={handleSaveClick}
            isLoading={validating === "save"}
            isDisabled={
              validating != null ||
              (config.ENVIRONMENT !== "dev" && reason?.length < 4)
            }
          >
            {template.id ? "Save" : "Create"}
          </Button>
        </Stack>
      </DialogActions>
      <TemplateValidationDialog
        open={validation != null}
        onClose={() => setValidation(null)}
        reports={validation?.report != null ? [validation.report] : []}
        onProceed={validation?.mode === "save" ? handleProceed : null}
        proceedLabel={needsForce ? "Save anyway" : "Save"}
        requireReason={errorCount > 0}
        reason={reason}
        onReasonChange={setReason}
        failureMessage={validation?.failureMessage ?? null}
        title={
          <Stack
            component={"span"}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <span>{"Template validation"}</span>
            <MuiButton
              variant={"text"}
              size={"small"}
              onClick={() => setCatalogueOpen(true)}
            >
              {"Template catalogue"}
            </MuiButton>
          </Stack>
        }
      />
      <TemplateCatalogueDialog
        open={catalogueOpen}
        onClose={() => setCatalogueOpen(false)}
        companyId={companyId}
      />
    </Dialog>
  );
};

export default TemplateEditor;
